import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DatabaseCacheService } from '@app/data/cache/database-cache.service';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { RecordData, TypesUtils } from 'src/app/types';
import { GostUtils } from '../cards/recordings/gost-utils';
import { GOST_I18N } from '../cards/recordings/gost.i18n';
import { normalizeRecordType } from '../user-record-list/record-type-display.util';
import { DetailsHeaderComponent } from '../measured-data-details/components/shared/details-header/details-header.component';

interface MatrixColumn {
  readonly offset: number;
  readonly recordId: string;
  readonly asOfDate: string;
  readonly createdAt: string;
  readonly measurerName: string;
  readonly estimatedFils: number | null;
  readonly foodRankings: Record<FoodRank, string>;
  readonly scLevel: number | null;
  readonly maxDrinkVolume: number | null;
  readonly aspirationRisk: string;
  readonly gcCautionItems: MatrixCautionItem[];
  readonly ocCautionItems: MatrixCautionItem[];
}

type MatrixCellTone = 'neutral' | 'warn' | 'down' | 'muted';
type MatrixCautionPillTone = 'bad' | 'acceptable';

interface MatrixCell {
  readonly text: string;
  readonly tone: MatrixCellTone;
  readonly pills?: MatrixCellPill[];
}

interface MatrixCellPill {
  readonly key: string;
  readonly text: string;
  readonly tone: MatrixCautionPillTone;
}

interface MatrixCautionItem {
  readonly label: string;
  readonly score: number;
  readonly index: number;
  readonly severityIndex: number;
}

interface MatrixDetailRow {
  readonly key: string;
  readonly label: string;
  readonly cells: MatrixCell[];
  readonly rowClass?: string;
}

type WindowSize = 5 | 10 | 20 | 30;

const FOOD_RANKS = [1, 2] as const;
type FoodRank = (typeof FOOD_RANKS)[number];

const CAUTION_METRIC_LABELS: Record<string, string> = {
  GC_CONSCIOUSNESS_LEVEL: GOST_I18N.gc.consciousnessLevel.label,
  GC_TEMPERATURE: GOST_I18N.gc.temperature.label,
  GC_PULSE_LEVEL: GOST_I18N.gc.pulseLevel.label,
  GC_SPO2: GOST_I18N.gc.spo2.label,
  GC_BREATH_LEVEL: GOST_I18N.gc.breathLevel.label,
  GC_ON_OXYGEN: GOST_I18N.gc.onOxygen.label,
  GC_SPUTUM: GOST_I18N.gc.sputum.label,
  OC_TEETH: GOST_I18N.oc.teeth.label,
  OC_DENTURE: GOST_I18N.oc.denture.label,
  OC_ORAL_HYGIENE: GOST_I18N.oc.oralHygiene.label,
  OC_ARTICULATION_LEVEL: GOST_I18N.oc.articulationLevel.label,
  OC_RIGHT_ARM: GOST_I18N.oc.rightArm.label,
  OC_LEFT_ARM: GOST_I18N.oc.leftArm.label,
  OC_DISABILITY_LEVEL: GOST_I18N.oc.disabilityLevel.label,
  OC_COGNITIVE_LEVEL: GOST_I18N.oc.cognitiveLevel.label,
};

const NO_MATCH_TEXT = GOST_I18N.common.noMatch;

@Component({
  selector: 'app-gost-matrix',
  templateUrl: './gost-matrix.component.html',
  styleUrls: ['./gost-matrix.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS, DetailsHeaderComponent],
  providers: [GostUtils],
  host: { class: 'gost-matrix-host' },
})
export class GostMatrixComponent implements OnInit {
  private static readonly PRINT_MODE_CLASS = 'gost-matrix-print-mode';
  private static readonly PRINT_STYLE_ID = 'gost-matrix-print-style';
  readonly headerTitle = $localize`:@@home.gostMatrix.header.title:GOST｜経過表`;
  readonly headerBadgeText = $localize`:@@home.gostMatrix.header.badge:評価`;
  readonly windowSizeOptions: readonly WindowSize[] = [5, 10, 20, 30];
  readonly skeletonColumns = Array(5);
  // 実テーブルの行構造を再現: 固定ラベル + data bone type
  readonly skeletonRows: readonly { label: string; type: 'date' | 'text' | 'num' | 'pills'; rowClass?: string }[] = [
    { label: $localize`:@@home.gostMatrix.row.asOfDate:評価日`, type: 'date' },
    { label: $localize`:@@home.gostMatrix.row.measurer:記録スタッフ`, type: 'text' },
    { label: $localize`:@@home.gostMatrix.row.estimatedFils:推定FILS`, type: 'num' },
    { label: $localize`:@@home.gostMatrix.row.foodRank1:食事形態（★★★★）`, type: 'text' },
    { label: $localize`:@@home.gostMatrix.row.foodRank2:食事形態（★★★☆）`, type: 'text' },
    { label: $localize`:@@home.gostMatrix.row.scLevel:SCレベル`, type: 'num' },
    { label: $localize`:@@home.gostMatrix.row.drinkVolume:飲水量`, type: 'num' },
    { label: $localize`:@@home.gostMatrix.row.aspirationRisk:誤嚥リスク`, type: 'text' },
    { label: $localize`:@@home.gostMatrix.row.gcCautions:注意項目（全身状態）`, type: 'pills' },
    { label: $localize`:@@home.gostMatrix.row.ocCautions:注意項目（口腔機能と生活能力）`, type: 'pills' },
    { label: $localize`:@@home.gostMatrix.row.createdAt:作成日`, type: 'date', rowClass: 'row--small' },
  ];

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly common = inject(DatabaseCacheService);
  private readonly gostUtils = inject(GostUtils);

  private userId = '';
  readonly isLoading = signal(true);
  readonly windowSize = signal<WindowSize>(5);
  readonly hoveredColIndex = signal<number | null>(null);
  private readonly records = signal<RecordData[]>([]);
  private readonly pendingDetailLoads = new Set<string>();

  readonly headerPatientName = computed(() => this.getHeaderPatientName());
  readonly headerMeasureDate = computed(() => this.getHeaderMeasureDate());
  readonly headerMeasurer = computed(() => this.getHeaderMeasurer());


  readonly visibleColumns = computed<MatrixColumn[]>(() => {
    const rows = this.records().slice(0, this.windowSize());
    return rows.map((record, index) => this.buildMatrixColumn(record, index));
  });

  readonly detailRows = computed<MatrixDetailRow[]>(() => {
    const columns = this.visibleColumns();
    const rankingRows: MatrixDetailRow[] = FOOD_RANKS.map((rank) => ({
      key: `foodRank${rank}`,
      label: this.getFoodRankLabel(rank),
      cells: columns.map((column) => this.createNeutralCell(column.foodRankings[rank])),
    }));

    return [
      {
        key: 'asOfDate',
        label: $localize`:@@home.gostMatrix.row.asOfDate:評価日`,
        cells: columns.map((column) => this.createNeutralCell(column.asOfDate)),
      },
      {
        key: 'measurer',
        label: $localize`:@@home.gostMatrix.row.measurer:記録スタッフ`,
        cells: columns.map((column) => this.createNeutralCell(column.measurerName)),
      },
      {
        key: 'estimatedFils',
        label: $localize`:@@home.gostMatrix.row.estimatedFils:推定FILS`,
        cells: columns.map((column) => this.createNumericCell(column.estimatedFils)),
      },
      ...rankingRows,
      {
        key: 'scLevel',
        label: $localize`:@@home.gostMatrix.row.scLevel:SCレベル`,
        cells: columns.map((column) => this.createNumericCell(column.scLevel)),
      },
      {
        key: 'drinkVolume',
        label: $localize`:@@home.gostMatrix.row.drinkVolume:飲水量`,
        cells: columns.map((column) => this.createVolumeCell(column.maxDrinkVolume)),
      },
      {
        key: 'aspirationRisk',
        label: $localize`:@@home.gostMatrix.row.aspirationRisk:誤嚥リスク`,
        cells: columns.map((column) => this.createNeutralCell(column.aspirationRisk)),
      },
      {
        key: 'gcCautions',
        label: $localize`:@@home.gostMatrix.row.gcCautions:注意項目（全身状態）`,
        cells: columns.map((column) => this.createCautionCell(column.gcCautionItems)),
      },
      {
        key: 'ocCautions',
        label: $localize`:@@home.gostMatrix.row.ocCautions:注意項目（口腔機能と生活能力）`,
        cells: columns.map((column) => this.createCautionCell(column.ocCautionItems)),
      },
      {
        key: 'createdAt',
        label: $localize`:@@home.gostMatrix.row.createdAt:作成日`,
        cells: columns.map((column) => this.createNeutralCell(column.createdAt)),
        rowClass: 'row--small',
      },
    ];
  });

  ngOnInit(): void {
    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshFromCache();
      });

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const userId = params['userId'];
      if (!userId) {
        return;
      }
      this.userId = userId;
      void this.common.setSelectedUserId(userId);
      this.isLoading.set(true);
      this.common.prepareUserGostList(userId, false);
    });
  }

  onRefresh(): void {
    if (!this.userId) {
      return;
    }
    this.isLoading.set(true);
    this.common.prepareUserGostList(this.userId, true);
  }

  async onPrint(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const cleanup = this.preparePrintContext();
    try {
      await this.waitForNextPaint();
      await this.waitForNextPaint();
      window.print();
    } finally {
      window.setTimeout(cleanup, 120000);
    }
  }

  private preparePrintContext(): () => void {
    const body = document.body;
    body.classList.add(GostMatrixComponent.PRINT_MODE_CLASS);
    const cleanupPrintPages = this.prepareMatrixPrintPages();

    const styleEl = document.createElement('style');
    styleEl.id = GostMatrixComponent.PRINT_STYLE_ID;
    styleEl.textContent = `
      @media print {
        @page {
          size: A4 landscape;
          margin: 8mm;
        }

        /* Hide everything except the print root */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} > *:not(.matrix-print-root) {
          display: none !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }

        /* Print root: body direct child, no flex/grid ancestors */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-root {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        /* Each page = one A4 landscape sheet */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          break-after: page;
          page-break-after: always;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page:last-child {
          break-after: auto;
          page-break-after: auto;
        }

        /* Page header (patient info + title) */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .header {
          margin-bottom: 4px !important;
          padding: 0 !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .header__right {
          display: none !important;
        }

        /* Table styles */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table {
          width: 100% !important;
          table-layout: auto !important;
          border-spacing: 0 !important;
          border-collapse: collapse !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table thead th,
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table__label-col {
          position: static !important;
          left: auto !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table th,
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table td {
          min-width: 90px !important;
          padding: 5px 10px !important;
          font-size: 11px !important;
          line-height: 1.4 !important;
          white-space: nowrap !important;
          vertical-align: top !important;
          border-bottom: 1px solid #e0e0e0 !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table__label-col {
          min-width: 140px !important;
          white-space: nowrap !important;
          font-weight: 500 !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table__col-badge {
          height: 20px !important;
          padding: 0 8px !important;
          font-size: 10px !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-table__col-badge-icon {
          display: none !important;
        }

        /* Pills */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-pills {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 3px !important;
          max-height: none !important;
          overflow: visible !important;
          white-space: normal !important;
        }

        body.${GostMatrixComponent.PRINT_MODE_CLASS} .matrix-print-page .matrix-pill {
          display: inline-block !important;
          height: auto !important;
          min-height: 18px !important;
          white-space: nowrap !important;
          line-height: 1.2 !important;
          padding: 2px 6px !important;
        }

        /* Hide overlays */
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .cdk-overlay-container,
        body.${GostMatrixComponent.PRINT_MODE_CLASS} .cdk-overlay-backdrop {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(styleEl);

    let disposed = false;
    const dispose = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      body.classList.remove(GostMatrixComponent.PRINT_MODE_CLASS);
      window.removeEventListener('afterprint', dispose);
      styleEl.remove();
      cleanupPrintPages();
    };

    window.addEventListener('afterprint', dispose, { once: true });
    return dispose;
  }

  private waitForNextPaint(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private prepareMatrixPrintPages(): () => void {
    const host = document.querySelector('app-gost-matrix');
    if (!(host instanceof HTMLElement)) {
      return () => {};
    }

    const matrixBody = host.querySelector('.matrix-body');
    const matrixTable = matrixBody?.querySelector('.matrix-table');
    if (!(matrixBody instanceof HTMLElement) || !(matrixTable instanceof HTMLTableElement)) {
      return () => {};
    }

    const headerRow = matrixTable.querySelector('thead tr');
    const totalColumns = headerRow?.children.length ?? 0;
    if (totalColumns <= 0) {
      return () => {};
    }

    const labelColumnCount = 1;
    const dataColumnCount = Math.max(totalColumns - labelColumnCount, 0);
    const columnsPerPage = 5;

    // Build patient header HTML from the details header
    const detailsHeader = host.querySelector('.gost-matrix__details-header');
    const patientHeader = document.querySelector('.patient-header__info') ?? document.querySelector('.m3-header__content');
    const headerHtml = (patientHeader?.cloneNode(true) as HTMLElement | null)?.outerHTML ?? '';
    const titleHtml = (detailsHeader?.cloneNode(true) as HTMLElement | null)?.outerHTML ?? '';

    // Create print root directly on body
    const printRoot = document.createElement('div');
    printRoot.className = 'matrix-print-root';

    const pageCount = Math.ceil(dataColumnCount / columnsPerPage);
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const start = pageIndex * columnsPerPage;
      const end = Math.min(start + columnsPerPage - 1, dataColumnCount - 1);
      const clonedTable = matrixTable.cloneNode(true) as HTMLTableElement;
      this.retainColumnsForPrint(clonedTable, start, end);

      const page = document.createElement('section');
      page.className = 'matrix-print-page';
      // Each page gets patient info + title + table
      page.innerHTML = headerHtml + titleHtml;
      // Hide PDF button and right section in cloned header
      page.querySelectorAll('.header__pdf, .header__right').forEach((el) => el.remove());
      page.appendChild(clonedTable);
      printRoot.appendChild(page);
    }

    document.body.appendChild(printRoot);

    return () => {
      printRoot.remove();
    };
  }

  private retainColumnsForPrint(table: HTMLTableElement, dataStart: number, dataEnd: number): void {
    const firstDataColumnIndex = 1;
    const keepStartIndex = firstDataColumnIndex + dataStart;
    const keepEndIndex = firstDataColumnIndex + dataEnd;

    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const cells = Array.from(row.children);
      cells.forEach((cell, cellIndex) => {
        const isLabelColumn = cellIndex === 0;
        const isDataColumnInRange = cellIndex >= keepStartIndex && cellIndex <= keepEndIndex;
        if (!isLabelColumn && !isDataColumnInRange) {
          cell.remove();
        }
      });
    });
  }

  onWindowSizeChange(value: WindowSize): void {
    this.windowSize.set(value);
    void this.ensureVisibleRecordDetails();
  }

  getColumnLabel(offset: number): string {
    if (offset === 0) {
      return $localize`:@@home.gostMatrix.column.latest:最新`;
    }
    return $localize`:@@home.gostMatrix.column.previous:前${offset}回`;
  }

  openRecord(recordId: string): void {
    const sectionId = this.common.getSelectedSection()?.sectionId;
    if (!sectionId || !this.userId || !recordId) {
      return;
    }
    this.router.navigate(
      ['/home', sectionId, 'users', this.userId, 'gost', 'records', recordId],
      { queryParams: { from: 'progress' } },
    );
  }

  private buildMatrixColumn(record: RecordData, offset: number): MatrixColumn {
    const rawData = record.data;
    const calculatedData = this.getCalculatedData(record);
    const scData = this.pickScData(calculatedData?.gostSCData, rawData?.gostSCData);
    const gcMetrics = this.pickMetrics(calculatedData?.gostGCData?.metrics, rawData?.gostGCData?.metrics);
    const ocMetrics = this.pickMetrics(calculatedData?.gostOCData?.metrics, rawData?.gostOCData?.metrics);
    const gcCautionItems = this.collectCautionItems(gcMetrics, 'GC');
    const ocCautionItems = this.collectCautionItems(ocMetrics, 'OC');

    return {
      offset,
      recordId: record.id,
      asOfDate: this.resolveText(calculatedData?.asOfDate ?? rawData?.asOfDate) || '-',
      createdAt: this.formatCreatedAt(calculatedData?.createdAtLocalStr ?? rawData?.createdAtLocalStr),
      measurerName: this.getMeasurerName(record),
      estimatedFils: this.resolveNumber(calculatedData?.filsGrade) ?? this.resolveNumber(rawData?.filsGrade),
      foodRankings: this.buildFoodRankings(calculatedData, rawData),
      scLevel: this.resolveNumber(scData?.level),
      maxDrinkVolume: this.getMaxMeasuredVolume(scData),
      aspirationRisk: this.getAspirationRiskText(calculatedData, rawData),
      gcCautionItems,
      ocCautionItems,
    };
  }

  private getCalculatedData(record: RecordData): any {
    const data = record.data;
    if (!data) {
      return data;
    }

    try {
      const clonedRecord = this.cloneRecord(record);
      this.normalizeRecordForEvaluation(clonedRecord);
      this.gostUtils.evaluateGost(clonedRecord);
      return clonedRecord.data;
    } catch {
      return data;
    }
  }

  private refreshFromCache(): void {
    const gostList = this.common.getUserGostsList();
    if (!gostList) {
      this.records.set([]);
      this.isLoading.set(true);
      return;
    }

    const converted = TypesUtils.convertJsonToRecords(gostList, true).records
      .filter((record) => normalizeRecordType(record.data?.recordType) === 'GOST');

    this.records.set(converted);
    if (converted.length === 0) {
      this.isLoading.set(false);
      return;
    }
    void this.ensureVisibleRecordDetails().then(() => {
      this.isLoading.set(false);
    });
  }

  private async ensureVisibleRecordDetails(): Promise<void> {
    const visible = this.records().slice(0, this.windowSize());
    if (!visible.length) {
      return;
    }

    const tasks = visible
      .filter((record) => !this.hasEvaluationPayload(record))
      .filter((record) => !this.pendingDetailLoads.has(record.id))
      .map((record) => this.loadRecordDetail(record.id));

    if (!tasks.length) {
      return;
    }

    await Promise.allSettled(tasks);
  }

  private hasEvaluationPayload(record: RecordData): boolean {
    const data = record?.data;
    if (!data || typeof data !== 'object') {
      return false;
    }

    const gcMetrics = this.normalizeMetrics(data?.gostGCData?.metrics);
    const ocMetrics = this.normalizeMetrics(data?.gostOCData?.metrics);
    const withoutSteps = this.extractScSteps(
      data?.gostSCData?.withoutThicknessSteps ?? data?.gostSCData?.withoutThickness,
    );
    const withSteps = this.extractScSteps(
      data?.gostSCData?.withThicknessSteps ?? data?.gostSCData?.withThickness,
    );

    return gcMetrics.length > 0 && ocMetrics.length > 0 && (withoutSteps.length > 0 || withSteps.length > 0);
  }

  private getHeaderPatientName(): string {
    const user = this.common.getSelectedUserWithUserData()?.user;
    if (!user) {
      return '';
    }
    return `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim();
  }

  private getHeaderMeasureDate(): string {
    const latest = this.records()[0];
    if (!latest) {
      return '';
    }
    const data = latest.data;
    return this.resolveText(data?.createdAtLocalStr ?? data?.asOfDate) || '';
  }

  private getHeaderMeasurer(): string {
    const latest = this.records()[0];
    const measurer = latest?.meta?.measurer;
    if (!measurer) {
      return '';
    }
    const fullName = `${measurer.lastName ?? ''}${measurer.firstName ?? ''}`.trim();
    if (!fullName) {
      return '';
    }
    return `${$localize`:@@home.gostMatrix.label.measurer:計測者`}: ${fullName}`;
  }

  private async loadRecordDetail(recordId: string): Promise<void> {
    if (!recordId) {
      return;
    }

    this.pendingDetailLoads.add(recordId);
    try {
      const loadedRecord = await this.common.getGostRecord(recordId);
      this.mergeLoadedRecord(loadedRecord as RecordData);
    } catch {
      // Keep list data when detail loading fails.
    } finally {
      this.pendingDetailLoads.delete(recordId);
    }
  }

  private mergeLoadedRecord(loadedRecord: RecordData | null | undefined): void {
    if (!loadedRecord?.id) {
      return;
    }

    const currentRecords = this.records();
    const index = currentRecords.findIndex((record) => record.id === loadedRecord.id);
    if (index < 0) {
      return;
    }

    const nextRecords = currentRecords.slice();
    nextRecords[index] = loadedRecord;
    this.records.set(nextRecords);
  }

  private resolveNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private cloneRecord(record: RecordData): RecordData {
    return JSON.parse(JSON.stringify(record)) as RecordData;
  }

  private normalizeRecordForEvaluation(record: RecordData): void {
    const data: any = record?.data ?? {};
    data.gostGCData = data.gostGCData ?? {};
    data.gostOCData = data.gostOCData ?? {};
    data.gostSCData = data.gostSCData ?? {};

    data.gostGCData.metrics = this.normalizeMetrics(data.gostGCData.metrics);
    data.gostOCData.metrics = this.normalizeMetrics(data.gostOCData.metrics);

    const normalizedWithout = this.extractScSteps(
      data.gostSCData.withoutThicknessSteps ?? data.gostSCData.withoutThickness,
    ).map((step) => this.normalizeScStep(step));
    const normalizedWith = this.extractScSteps(
      data.gostSCData.withThicknessSteps ?? data.gostSCData.withThickness,
    ).map((step) => this.normalizeScStep(step));

    data.gostSCData.withoutThicknessSteps = normalizedWithout;
    data.gostSCData.withThicknessSteps = normalizedWith;

    record.data = data;
  }

  private normalizeScStep(step: any): any {
    if (!step || typeof step !== 'object') {
      return { amount: null, rounds: [] };
    }
    const rounds = Array.isArray(step.rounds)
      ? step.rounds.filter((round) => !!round && typeof round === 'object')
      : [];
    return {
      ...step,
      rounds,
    };
  }

  private resolveText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  }

  private pickScData(calculatedScData: any, rawScData: any): any {
    if (this.hasScContent(calculatedScData)) {
      return calculatedScData;
    }
    return rawScData ?? calculatedScData;
  }

  private hasScContent(scData: any): boolean {
    if (!scData || typeof scData !== 'object') {
      return false;
    }
    if (this.resolveNumber(scData?.level) !== null) {
      return true;
    }
    if (this.extractScSteps(scData?.withoutThicknessSteps ?? scData?.withoutThickness).length > 0) {
      return true;
    }
    if (this.extractScSteps(scData?.withThicknessSteps ?? scData?.withThickness).length > 0) {
      return true;
    }
    return false;
  }

  private pickMetrics(calculatedMetrics: any, rawMetrics: any): any[] {
    const calculated = this.normalizeMetrics(calculatedMetrics);
    if (calculated.length > 0) {
      return calculated;
    }
    return this.normalizeMetrics(rawMetrics);
  }

  private normalizeMetrics(metrics: any): any[] {
    if (Array.isArray(metrics)) {
      return metrics;
    }
    if (metrics && typeof metrics === 'object') {
      return Object.values(metrics);
    }
    return [];
  }

  private getMeasurerName(record: RecordData): string {
    const recorderName = this.resolveText(record.data?.recorderName);
    if (recorderName) {
      return recorderName;
    }
    const metaName = `${record.meta?.measurer?.lastName ?? ''} ${record.meta?.measurer?.firstName ?? ''}`.trim();
    return metaName || '-';
  }

  private getFoodRankLabel(rank: FoodRank): string {
    switch (rank) {
      case 1:
        return $localize`:@@home.gostMatrix.row.foodRank1:食事形態（★★★★）`;
      case 2:
        return $localize`:@@home.gostMatrix.row.foodRank2:食事形態（★★★☆）`;
      default:
        return $localize`:@@home.gostMatrix.row.foodRankFallback:食事形態`;
    }
  }

  private buildFoodRankings(primaryData: any, fallbackData: any): Record<FoodRank, string> {
    const rankings: Record<FoodRank, string> = {
      1: '-',
      2: '-',
    };

    const mergeRankings = (data: any) => {
      const foods = data?.PrimarilySelectedFoods?.selectedFoods;
      if (!Array.isArray(foods)) {
        return;
      }
      for (const food of foods) {
        const rank = Number(food?.rank);
        const name = this.resolveText(food?.name);
        if (!name || name === NO_MATCH_TEXT || !FOOD_RANKS.includes(rank as FoodRank)) {
          continue;
        }
        if (rankings[rank as FoodRank] === '-') {
          rankings[rank as FoodRank] = name;
        }
      }
    };

    mergeRankings(primaryData);
    mergeRankings(fallbackData);

    const fallbackRecommendation = this.resolveText(
      primaryData?.gostRecommendation ??
      primaryData?.filsRecommendation ??
      fallbackData?.gostRecommendation ??
      fallbackData?.filsRecommendation,
    );
    if (rankings[1] === '-' && fallbackRecommendation && fallbackRecommendation !== NO_MATCH_TEXT) {
      rankings[1] = fallbackRecommendation;
    }

    return rankings;
  }

  private getMaxMeasuredVolume(scData: any): number | null {
    if (!scData) {
      return null;
    }

    const withoutSteps = this.extractScSteps(scData?.withoutThicknessSteps ?? scData?.withoutThickness);
    const withSteps = this.extractScSteps(scData?.withThicknessSteps ?? scData?.withThickness);
    const steps = [
      ...withoutSteps,
      ...withSteps,
    ];

    let max: number | null = null;
    for (const step of steps) {
      const amount = this.resolveAmount(step?.amount);
      if (amount === null) {
        continue;
      }
      const measured = (step?.rounds ?? []).some((round: any) => this.isRoundMeasured(round));
      if (measured) {
        max = max === null ? amount : Math.max(max, amount);
      }
    }

    return max;
  }

  private getAspirationRiskText(primaryData: any, fallbackData: any): string {
    const fromPrimary = this.buildAspirationRiskText(primaryData);
    if (fromPrimary) {
      return fromPrimary;
    }
    const fromFallback = this.buildAspirationRiskText(fallbackData);
    if (fromFallback) {
      return fromFallback;
    }
    return NO_MATCH_TEXT;
  }

  private buildAspirationRiskText(data: any): string {
    const dss = this.resolveNumber(data?.dss);
    const dssDesc = this.resolveText(data?.dssDesc);
    if (dss !== null && dssDesc) {
      return `Lv.${dss}（${dssDesc}）`;
    }
    if (dss !== null) {
      return `Lv.${dss}`;
    }
    if (dssDesc) {
      return dssDesc;
    }
    return '';
  }

  private collectCautionItems(
    metrics: any[],
    section: 'GC' | 'OC',
  ): MatrixCautionItem[] {
    if (!Array.isArray(metrics)) {
      return [];
    }

    const scoredMetrics = metrics
      .map((metric, index) => ({
        metric,
        index,
        score: this.resolveNumber(metric?.score),
      }))
      .filter((entry) => entry.score !== null) as {
      metric: any;
      index: number;
      score: number;
    }[];

    if (!scoredMetrics.length) {
      return [];
    }

    const uniqueScores = [...new Set(scoredMetrics.map((entry) => entry.score))].sort((a, b) => a - b);
    const hasNegativeScale = scoredMetrics.some((entry) => entry.score < 0);
    const maxScore = scoredMetrics.reduce(
      (max, entry) => (entry.score > max ? entry.score : max),
      Number.NEGATIVE_INFINITY,
    );

    const items: MatrixCautionItem[] = [];
    scoredMetrics.forEach(({ metric, index, score }) => {
      const isCaution = hasNegativeScale ? score < 0 : score < maxScore;
      if (!isCaution) {
        return;
      }
      const code = this.resolveText(metric?.code);
      if (!code) {
        return;
      }
      const label = (CAUTION_METRIC_LABELS[code] ?? `${section}:${code}`).replace(/\s+/g, ' ').trim();
      const severityIndex = Math.max(1, uniqueScores.indexOf(score) + 1);
      items.push({ label, score, index, severityIndex });
    });

    return items.sort((a, b) =>
      a.severityIndex - b.severityIndex || a.score - b.score || a.index - b.index,
    );
  }

  private extractScSteps(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== 'object') {
      return [];
    }
    if (Array.isArray(value.steps)) {
      return value.steps;
    }
    const values = Object.values(value);
    if (values.some((entry) => !!entry && typeof entry === 'object')) {
      return values as any[];
    }
    return [];
  }

  private resolveAmount(value: unknown): number | null {
    const direct = this.resolveNumber(value);
    if (direct !== null) {
      return direct;
    }
    if (typeof value !== 'string') {
      return null;
    }
    const matched = value.match(/-?\d+(\.\d+)?/);
    if (!matched) {
      return null;
    }
    const parsed = Number(matched[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private isRoundMeasured(round: any): boolean {
    if (!round) {
      return false;
    }
    if (typeof round.isMeasured === 'boolean') {
      return round.isMeasured;
    }
    const keys = [
      'swallowResult',
      'coughResult',
      'swallowDelay',
      'abnormalSound',
      'multiSwallows',
      'throatClear',
      'otherAbbration',
      'otherAbnormality',
      'testResult',
    ] as const;
    return keys.some((key) => {
      const value = round[key];
      return value !== null && value !== undefined && value !== '';
    });
  }

  private createNeutralCell(text: string): MatrixCell {
    return { text: text || '-', tone: 'neutral' };
  }

  private createNumericCell(value: number | null): MatrixCell {
    if (value === null) {
      return { text: '-', tone: 'muted' };
    }
    return { text: String(value), tone: 'neutral' };
  }

  private createVolumeCell(value: number | null): MatrixCell {
    if (value === null) {
      return { text: NO_MATCH_TEXT, tone: 'muted' };
    }
    return { text: `${value} mL`, tone: 'neutral' };
  }

  private createCautionCell(items: MatrixCautionItem[]): MatrixCell {
    if (!items.length) {
      return { text: NO_MATCH_TEXT, tone: 'muted', pills: [] };
    }

    const pills: MatrixCellPill[] = items.map((item) => ({
      key: `${item.index}-${item.score}`,
      text: item.label,
      tone: this.getCautionPillTone(item),
    }));

    return { text: '', tone: 'neutral', pills };
  }

  private formatCreatedAt(value: unknown): string {
    const text = this.resolveText(value);
    if (!text) {
      return '-';
    }
    // Strip seconds: "2025-03-01 14:30:00" → "2025-03-01 14:30"
    return text.replace(/:\d{2}$/, '');
  }

  private getCautionPillTone(item: MatrixCautionItem): MatrixCautionPillTone {
    if (item.score <= -3 || item.severityIndex <= 1) {
      return 'bad';
    }
    return 'acceptable';
  }
}
